import { useState, useCallback, useEffect } from 'react'
import { Search, ChevronLeft, ChevronRight, Download } from 'lucide-react'
import type { LeadDetail, PaginationInfo } from '../types'
import { fetchLeads, getExportUrl } from '../api'

interface Props {
  initialLeads: LeadDetail[]
  periodMonth: string
  totalLeads: number
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '-'
  const d = new Date(dateStr)
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

function formatDuration(seconds: number | null): string {
  if (!seconds) return '-'
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return `${m}:${s.toString().padStart(2, '0')}`
}

function formatCurrency(val: number | null): string {
  if (val === null || val === 0) return '-'
  return `$${val.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

export function AdWordsLeadsTable({ initialLeads, periodMonth, totalLeads }: Props) {
  const [leads, setLeads] = useState<LeadDetail[]>(initialLeads)
  const [pagination, setPagination] = useState<PaginationInfo>({
    page: 1, per_page: 25, total: totalLeads, total_pages: Math.ceil(totalLeads / 25),
  })
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(false)

  const loadPage = useCallback(async (page: number, searchQuery?: string) => {
    setLoading(true)
    try {
      const result = await fetchLeads({ period_month: periodMonth, page, per_page: 25, search: searchQuery || undefined })
      setLeads(result.leads)
      setPagination(result.pagination)
    } catch { /* keep */ } finally { setLoading(false) }
  }, [periodMonth])

  useEffect(() => {
    const timer = setTimeout(() => { loadPage(1, search || undefined) }, 300)
    return () => clearTimeout(timer)
  }, [search, loadPage])

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
      <div className="flex items-center justify-between p-5 border-b border-gray-200">
        <h3 className="text-sm font-semibold text-gray-700">CallRail Lead Details</h3>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input type="text" placeholder="Search name, phone, job #..."
              value={search} onChange={(e) => setSearch(e.target.value)}
              className="pl-9 pr-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-64" />
          </div>
          <a href={getExportUrl(periodMonth)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-white border border-gray-300 text-gray-700 hover:bg-gray-50">
            <Download className="w-3.5 h-3.5" /> Export CSV
          </a>
        </div>
      </div>

      <div className={`overflow-x-auto ${loading ? 'opacity-50' : ''}`}>
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="px-4 py-3 text-left font-medium text-gray-500">Date</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Caller</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Phone</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Duration</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Classification</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Job #</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Job Status</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Tech</th>
              <th className="px-4 py-3 text-right font-medium text-gray-500">Payment</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {leads.map((lead) => (
              <tr key={lead.id} className="hover:bg-gray-50">
                <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{formatDate(lead.activity_date)}</td>
                <td className="px-4 py-3 text-gray-900 font-medium whitespace-nowrap">{lead.caller_name || '-'}</td>
                <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{lead.caller_number || '-'}</td>
                <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{formatDuration(lead.call_duration)}</td>
                <td className="px-4 py-3 whitespace-nowrap">
                  {lead.classification && (
                    <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${
                      lead.classification === 'relevant' ? 'bg-green-100 text-green-700'
                        : lead.classification === 'irrelevant' ? 'bg-red-100 text-red-700'
                        : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {lead.classification}
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                  {lead.job_number ? (
                    <span className="font-mono text-blue-600">#{lead.job_number}</span>
                  ) : <span className="text-gray-400">-</span>}
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  {lead.job_status ? (
                    <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${
                      lead.job_status === 'Done' ? 'bg-green-100 text-green-700'
                        : lead.job_status.includes('Cancel') ? 'bg-red-100 text-red-700'
                        : 'bg-yellow-100 text-yellow-700'
                    }`}>{lead.job_status}</span>
                  ) : <span className="text-gray-400">-</span>}
                </td>
                <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{lead.tech || '-'}</td>
                <td className="px-4 py-3 text-right text-gray-900 font-medium whitespace-nowrap">{formatCurrency(lead.payment_amount)}</td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${
                    lead.payment_status === 'Paid' ? 'bg-green-100 text-green-700'
                      : lead.payment_status === 'Unpaid' ? 'bg-yellow-100 text-yellow-700'
                      : 'bg-gray-100 text-gray-500'
                  }`}>{lead.payment_status}</span>
                </td>
              </tr>
            ))}
            {leads.length === 0 && (
              <tr><td colSpan={10} className="px-4 py-8 text-center text-gray-400">No leads found</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {pagination.total_pages > 1 && (
        <div className="flex items-center justify-between px-5 py-3 border-t border-gray-200">
          <span className="text-sm text-gray-500">
            Showing {(pagination.page - 1) * pagination.per_page + 1}-{Math.min(pagination.page * pagination.per_page, pagination.total)} of {pagination.total}
          </span>
          <div className="flex items-center gap-2">
            <button onClick={() => loadPage(pagination.page - 1, search || undefined)} disabled={pagination.page <= 1}
              className="p-1.5 rounded-md border border-gray-300 text-gray-500 hover:bg-gray-50 disabled:opacity-30">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-sm text-gray-600">Page {pagination.page} of {pagination.total_pages}</span>
            <button onClick={() => loadPage(pagination.page + 1, search || undefined)} disabled={pagination.page >= pagination.total_pages}
              className="p-1.5 rounded-md border border-gray-300 text-gray-500 hover:bg-gray-50 disabled:opacity-30">
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
